export interface LineOperationsLiver {
  id: string
  display_name?: string | null
  status?: string | null
}

export interface LineOperationsImport {
  id: string
  liver_id: string
  file_name: string
  message_count: number
  active_days: number
  date_start: string | null
  date_end: string | null
  primary_manager_name: string | null
  handoff_note: string | null
  analysis?: unknown
  created_at: string
  updated_at: string
}

export interface LineOperationsRow {
  liverId: string
  liverName: string
  importCount: number
  totalMessageCount: number
  activeDays: number
  dateStart: string | null
  dateEnd: string | null
  latestImportedAt: string | null
  latestImportId: string | null
  fileName: string | null
  primaryManagerName: string | null
  handoffNote: string | null
  riskCount: number
  highRiskCount: number
  handoffSignalCount: number
  topicLabels: string[]
  isStale: boolean
  needsReview: boolean
}

export interface LineOperationsDashboard {
  liverCount: number
  importedLiverCount: number
  missingImportCount: number
  totalImportCount: number
  totalMessageCount: number
  riskImportCount: number
  handoffImportCount: number
  staleImportCount: number
  rows: LineOperationsRow[]
}

type LineAnalysis = {
  risks?: Array<{ severity?: string | null } | null> | null
  handoffSignals?: unknown[] | null
  topics?: Array<{ topic?: string | null } | null> | null
}

const STALE_DAYS = 60

const TOPIC_LABELS: Record<string, string> = {
  onboarding: 'オンボーディング',
  schedule_call: '日程・通話',
  payment_tax: '支払・税務',
  event: 'イベント',
  rights_rules: '権利・ルール',
  motivation: 'モチベーション'
}

export function buildLineOperations(
  imports: LineOperationsImport[],
  livers: LineOperationsLiver[],
  now: Date
): LineOperationsDashboard {
  const activeLivers = livers.filter((liver) => liver.status !== 'graduated')
  const importsByLiver = groupImportsByLiver(imports)
  const rows = activeLivers
    .map((liver) => buildLineOperationsRow(liver, importsByLiver.get(liver.id) ?? [], now))
    .sort(compareLineRows)

  const importedRows = rows.filter((row) => row.importCount > 0)

  return {
    liverCount: activeLivers.length,
    importedLiverCount: importedRows.length,
    missingImportCount: rows.filter((row) => row.importCount === 0).length,
    totalImportCount: imports.length,
    totalMessageCount: imports.reduce((sum, lineImport) => sum + safeNumber(lineImport.message_count), 0),
    riskImportCount: importedRows.filter((row) => row.riskCount > 0).length,
    handoffImportCount: importedRows.filter((row) => row.handoffSignalCount > 0 || Boolean(row.handoffNote)).length,
    staleImportCount: importedRows.filter((row) => row.isStale).length,
    rows
  }
}

function buildLineOperationsRow(
  liver: LineOperationsLiver,
  imports: LineOperationsImport[],
  now: Date
): LineOperationsRow {
  const sorted = [...imports].sort((a, b) => compareDateDesc(a.created_at, b.created_at))
  const latest = sorted[0] ?? null
  const analysis = readAnalysis(latest?.analysis)
  const riskCount = analysis.risks?.length ?? 0
  const highRiskCount = (analysis.risks ?? []).filter((risk) => risk?.severity === 'high').length
  const handoffSignalCount = analysis.handoffSignals?.length ?? 0
  const topicLabels = [...new Set((analysis.topics ?? []).flatMap((topic) => topicLabel(topic?.topic)))].slice(0, 4)
  const dateEnd = latest?.date_end ?? null
  const isStale = dateEnd ? daysBetween(dateEnd, now) >= STALE_DAYS : imports.length > 0
  const needsReview = riskCount > 0 || handoffSignalCount > 0 || Boolean(latest?.handoff_note) || isStale

  return {
    liverId: liver.id,
    liverName: liver.display_name ?? '名称未設定',
    importCount: imports.length,
    totalMessageCount: imports.reduce((sum, lineImport) => sum + safeNumber(lineImport.message_count), 0),
    activeDays: imports.reduce((sum, lineImport) => sum + safeNumber(lineImport.active_days), 0),
    dateStart: minDate(sorted.map((lineImport) => lineImport.date_start)),
    dateEnd: maxDate(sorted.map((lineImport) => lineImport.date_end)),
    latestImportedAt: latest?.created_at ?? null,
    latestImportId: latest?.id ?? null,
    fileName: latest?.file_name ?? null,
    primaryManagerName: latest?.primary_manager_name ?? null,
    handoffNote: latest?.handoff_note ?? null,
    riskCount,
    highRiskCount,
    handoffSignalCount,
    topicLabels,
    isStale,
    needsReview
  }
}

function groupImportsByLiver(imports: LineOperationsImport[]): Map<string, LineOperationsImport[]> {
  const result = new Map<string, LineOperationsImport[]>()
  for (const lineImport of imports) {
    const list = result.get(lineImport.liver_id) ?? []
    list.push(lineImport)
    result.set(lineImport.liver_id, list)
  }
  return result
}

function readAnalysis(value: unknown): LineAnalysis {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  const src = value as LineAnalysis
  return {
    risks: Array.isArray(src.risks) ? src.risks : [],
    handoffSignals: Array.isArray(src.handoffSignals) ? src.handoffSignals : [],
    topics: Array.isArray(src.topics) ? src.topics : []
  }
}

function topicLabel(topic: string | null | undefined): string[] {
  if (!topic) return []
  return [TOPIC_LABELS[topic] ?? topic]
}

function compareLineRows(a: LineOperationsRow, b: LineOperationsRow): number {
  return Number(b.needsReview) - Number(a.needsReview) ||
    b.highRiskCount - a.highRiskCount ||
    b.riskCount - a.riskCount ||
    b.handoffSignalCount - a.handoffSignalCount ||
    Number(b.importCount === 0) - Number(a.importCount === 0) ||
    compareNullableDateDesc(a.latestImportedAt, b.latestImportedAt) ||
    a.liverName.localeCompare(b.liverName, 'ja')
}

function compareDateDesc(a: string, b: string): number {
  return new Date(b).getTime() - new Date(a).getTime()
}

function compareNullableDateDesc(a: string | null, b: string | null): number {
  if (a && b) return compareDateDesc(a, b)
  if (a) return -1
  if (b) return 1
  return 0
}

function minDate(values: Array<string | null>): string | null {
  return values.filter(isString).sort()[0] ?? null
}

function maxDate(values: Array<string | null>): string | null {
  return values.filter(isString).sort().at(-1) ?? null
}

function isString(value: string | null): value is string {
  return typeof value === 'string' && value.length > 0
}

function daysBetween(date: string, now: Date): number {
  const start = new Date(`${date}T00:00:00+09:00`).getTime()
  if (!Number.isFinite(start)) return 0
  return Math.floor((now.getTime() - start) / 86400000)
}

function safeNumber(value: number): number {
  return Number.isFinite(value) ? value : 0
}
